
import { Card, CardHeader, CardTitle, CardContent } from '@deepguide-ai/ui/card';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { EvidencePackViewerDTO } from '@deepguide-ai/types';
import { Video } from 'lucide-react';
import { FileExtensionIcon } from '@/utils/file-icons';


export  function ArtifactsPanel({
  artifacts,
  selectedFile,
  onSelectFile,
  layout="horizontal"
}: {
  artifacts: EvidencePackViewerDTO['artifacts'];
  selectedFile: { type: 'screenshot' | 'attachment'; id: string } | null;
  onSelectFile: (file: { type: 'screenshot' | 'attachment'; id: string } | null) => void;
  layout?: 'horizontal' | 'vertical';
}) {
  const { screenshots, attachments, video } = artifacts;

  return (
    <Card className='gap-3 shadow-none rounded-sm'>
      <CardHeader className="px-4">
        <CardTitle className="text-sm">Evidence Files</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea  type="always" viewClassName={` ${layout === 'vertical' ? 'max-h-[360px] h-full' : 'h-[360px]'} [&>div]:!block`}>
          <div className="space-y-4 px-4 ">
            {/* Video */}
            {video && (
              <div>
                <div className="text-xs mb-2 font-medium text-muted-foreground ">
                  Replay Video
                </div>
                <button
                  onClick={() => onSelectFile({ type: 'attachment', id: 'video' })}
                  className={cn(
                    'w-full flex items-center gap-3 p-2 rounded-md border transition-colors',
                    selectedFile?.id === 'video'
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:bg-muted/50'
                  )}
                >
                  <Video className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm flex-1 text-left">Session Recording</span>
                  {video.duration && (
                    <span className="text-xs text-muted-foreground">
                      {Math.round(video.duration / 1000)}s
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Screenshots */}
            {screenshots.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  Screenshots ({screenshots.length})
                </div>
                <div className={cn('grid gap-3', layout === 'vertical' ? 'grid-cols-1' : 'grid-cols-2')}>
                  {screenshots.map((screenshot) => (
                    <div
                      key={screenshot.id}
                      onClick={() => onSelectFile({ type: 'screenshot', id: screenshot.id })}
                      className={cn(
                        'aspect-video rounded-md cursor-pointer overflow-hidden border transition-all',
                        selectedFile?.type === 'screenshot' && selectedFile.id === screenshot.id
                          ? 'ring-2 ring-primary'
                          : 'hover:ring-1 hover:ring-muted-foreground/30'
                      )}
                    >
                      <img
                        src={screenshot.thumbnailUrl || screenshot.url}
                        alt={`Screenshot ${screenshot.stepNumber || ''}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  Attachments ({attachments.length})
                </div>
                <div className="space-y-1">
                  {attachments.map((attachment) => {
                    // const fileType = getFileType(attachment.mimeType, attachment.filename);
                    return (
                      <button
                        key={attachment.id}
                        onClick={() => onSelectFile({ type: 'attachment', id: attachment.id })}
                        className={cn(
                          'w-full flex items-center cursor-pointer gap-2 p-2 rounded-md border transition-colors min-w-0',
                          selectedFile?.type === 'attachment' && selectedFile.id === attachment.id
                            ? 'border-primary bg-primary/5'
                            : 'border-transparent hover:bg-muted/50'
                        )}
                      >
                        <FileExtensionIcon filename={attachment.filename} className="h-4 w-4 shrink-0"  />
                        <span
                          className="text-sm flex-1 min-w-0 text-left truncate break-all"
                          title={attachment.filename}
                        >
                          {attachment.filename}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {(attachment.mimeType === 'application/policy' ||
                            attachment.mimeType === 'application/vault-document') &&
                          attachment.elementId
                            ? attachment.elementId
                            : `${(attachment.size / 1024).toFixed(1)} KB`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {screenshots.length === 0 && attachments.length === 0 && !video && (
              <div className="text-sm text-muted-foreground text-center py-8">
                No evidence files
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
export default ArtifactsPanel;
